import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const CalendarPage = () => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [leaves, setLeaves] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [leavesRes, holidaysRes] = await Promise.all([
          api.get('/leaves').catch(() => ({ data: [] })),
          api.get('/holidays', { params: { year } }).catch(() => ({ data: [] })),
        ]);
        setLeaves(Array.isArray(leavesRes.data) ? leavesRes.data : []);
        setHolidays(Array.isArray(holidaysRes.data) ? holidaysRes.data : []);
      } catch (err) {
        console.error('Calendar fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [year]);

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  };

  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  };

  const goToday = () => {
    setMonth(today.getMonth());
    setYear(today.getFullYear());
  };

  // Build calendar grid
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const calendarDays = [];

  // Previous month fill
  for (let i = firstDay - 1; i >= 0; i--) {
    calendarDays.push({ day: daysInPrevMonth - i, currentMonth: false });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    calendarDays.push({ day: d, currentMonth: true });
  }
  // Next month fill
  const remaining = 42 - calendarDays.length;
  for (let d = 1; d <= remaining; d++) {
    calendarDays.push({ day: d, currentMonth: false });
  }

  // Helper: normalize date to YYYY-MM-DD string
  const toDateStr = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Check if a date falls within a leave range
  const getLeavesForDate = (dateStr) => {
    return leaves.filter(l => {
      const from = toDateStr(l.fromDate);
      const to = toDateStr(l.toDate);
      return dateStr >= from && dateStr <= to;
    });
  };

  // Check if a date is a holiday
  const getHolidaysForDate = (dateStr) => {
    return holidays.filter(h => toDateStr(h.date) === dateStr);
  };

  const isToday = (day) => {
    return day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
  };

  const isSunday = (index) => index % 7 === 0;

  // Selected day details
  const selectedDateStr = selectedDay
    ? `${year}-${String(month + 1).padStart(2, '0')}-${String(selectedDay).padStart(2, '0')}`
    : null;
  const selectedLeaves = selectedDateStr ? getLeavesForDate(selectedDateStr) : [];
  const selectedHolidays = selectedDateStr ? getHolidaysForDate(selectedDateStr) : [];

  if (loading) return <div className="loading">Loading calendar...</div>;

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <div className="calendar-nav">
          <button className="btn-sm btn-secondary" onClick={prevMonth}><FiChevronLeft /></button>
          <h2>{MONTHS[month]} {year}</h2>
          <button className="btn-sm btn-secondary" onClick={nextMonth}><FiChevronRight /></button>
        </div>
        <button className="btn-sm btn-primary" onClick={goToday}>Today</button>
      </div>

      <div className="calendar-grid">
        {DAYS.map(d => (
          <div key={d} className={`calendar-day-header ${d === 'Sun' ? 'sunday' : ''}`}>{d}</div>
        ))}

        {calendarDays.map((cell, idx) => {
          if (!cell.currentMonth) {
            return <div key={`other-${idx}`} className="calendar-day other-month">{cell.day}</div>;
          }

          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(cell.day).padStart(2, '0')}`;
          const dayLeaves = getLeavesForDate(dateStr);
          const dayHolidays = getHolidaysForDate(dateStr);
          const hasEvents = dayLeaves.length > 0 || dayHolidays.length > 0;
          const sunday = isSunday(idx);

          return (
            <div
              key={`day-${cell.day}`}
              className={`calendar-day ${isToday(cell.day) ? 'today' : ''} ${hasEvents ? 'has-events' : ''} ${sunday ? 'sunday' : ''} ${selectedDay === cell.day ? 'selected' : ''}`}
              onClick={() => setSelectedDay(selectedDay === cell.day ? null : cell.day)}
            >
              <span className="calendar-day-number">{cell.day}</span>
              <div className="calendar-markers">
                {dayHolidays.map((h, i) => (
                  <span key={`h-${i}`} className="calendar-marker holiday" title={h.name}>
                    {h.name.length > 10 ? h.name.slice(0, 10) + '…' : h.name}
                  </span>
                ))}
                {dayLeaves.map((l, i) => (
                  <span key={`l-${i}`} className={`calendar-marker ${l.status.toLowerCase()}`} title={`${l.employee?.name} · ${l.leaveType} - ${l.status}`}>
                    {l.employee?.name ? `${l.employee.name.split(' ')[0]}: ` : ''}{l.leaveType}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="calendar-legend">
        <div className="legend-item"><span className="legend-dot holiday"></span> Holiday</div>
        <div className="legend-item"><span className="legend-dot approved"></span> Approved Leave</div>
        <div className="legend-item"><span className="legend-dot pending"></span> Pending Leave</div>
        <div className="legend-item"><span className="legend-dot rejected"></span> Rejected Leave</div>
        <div className="legend-item"><span className="legend-dot today-dot"></span> Today</div>
        <div className="legend-item"><span className="legend-dot sunday-dot"></span> Sunday</div>
      </div>

      {/* Selected day detail panel */}
      {selectedDay && (selectedLeaves.length > 0 || selectedHolidays.length > 0) && (
        <div className="calendar-detail">
          <div className="calendar-detail-header">
            <h3>{selectedDay} {MONTHS[month]} {year}</h3>
            <button className="btn-sm btn-secondary" onClick={() => setSelectedDay(null)}><FiX /></button>
          </div>

          {selectedHolidays.map((h, i) => (
            <div key={`dh-${i}`} className="calendar-detail-item holiday-detail">
              <span className="detail-badge holiday">{h.type}</span>
              <strong>{h.name}</strong>
              {h.description && <p>{h.description}</p>}
            </div>
          ))}

          {selectedLeaves.map((l, i) => (
            <div key={`dl-${i}`} className={`calendar-detail-item leave-detail ${l.status.toLowerCase()}`}>
              <span className={`detail-badge ${l.status.toLowerCase()}`}>{l.status}</span>
              <strong>{l.leaveType} Leave</strong>
              {l.employee?.name && <p style={{ margin: '2px 0', fontWeight: 600, color: '#4f46e5' }}>{l.employee.name}</p>}
              <p>{new Date(l.fromDate).toLocaleDateString()} — {new Date(l.toDate).toLocaleDateString()}</p>
              {l.reason && <p className="detail-reason">{l.reason}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
